import { Icon } from '../../components/Icon';
import { OnScreenKeyboard } from '../../components/OnScreenKeyboard';
import type { BoardProps, InputKey } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { letterStates, misses, type PetalMove, type PetalState } from './logic';

export function PetalBoard({ state, status, dispatch, feedback }: BoardProps<PetalState, PetalMove>) {
  const playing = status === 'playing';
  const left = state.maxMisses - misses(state);
  const ls = letterStates(state);
  const lastError = feedback?.tone === 'error' ? feedback.nonce : 0;

  const disabledKeys: Record<string, boolean> = {};
  state.guessed.forEach((l) => {
    disabledKeys[l] = true;
  });

  const onKey = (key: InputKey) => {
    if (key.type === 'char') dispatch({ type: 'guess', ch: key.value });
  };

  return (
    <div className="cv-board">
      <div className="cv-petal">
        <div className="cv-petal__lives" aria-label={`${left} of ${state.maxMisses} petals left`}>
          {Array.from({ length: state.maxMisses }, (_, i) => (
            <span key={i} className="cv-petal__petal" data-gone={i >= left ? 'true' : undefined}>
              <Icon name="flower" size={22} />
            </span>
          ))}
        </div>

        <div
          key={`word-${lastError}`}
          className={cx('cv-petal__word', lastError && 'cv-anim-shake')}
          aria-label="Word to guess"
        >
          {state.answer.split('').map((ch, i) => {
            const shown = state.guessed.includes(ch);
            return (
              <div
                key={i}
                className="cv-tile cv-petal__slot"
                data-state={shown ? 'correct' : undefined}
                data-blank={shown ? undefined : 'true'}
                aria-label={shown ? ch : 'blank'}
              >
                {shown ? ch.toUpperCase() : ''}
              </div>
            );
          })}
        </div>
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>

      <OnScreenKeyboard
        onKey={onKey}
        letterStates={ls}
        disabledKeys={disabledKeys}
        disabled={!playing}
        compact
      />
    </div>
  );
}
